// Purpose: Normalize Firestore submission docs into a single in-memory shape for UI/metrics.

import { DEFAULT_NURSING_LAYOUT_TITLES } from '../lib/crossAuditSlot';

type AnyDoc = Record<string, any>;

const toString = (v: unknown): string =>
  v == null ? "" : typeof v === "number" ? String(v) : typeof v === "string" ? v : String(v);

const toStrArray = (arr: unknown[]): string[] =>
  arr.map((v) => toString(v));

const isNestedArrayWrapper = (value: unknown): value is { firestoreNestedArray: true; items: unknown[] } =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  (value as { firestoreNestedArray?: unknown }).firestoreNestedArray === true &&
  Array.isArray((value as { items?: unknown }).items);

/** One chart row: strings, numeric 1/0 from imports, or Firestore nested-array cells. */
const coerceAnswerCell = (cell: unknown): string => {
  if (cell == null) return "";
  if (typeof cell === "string") return cell;
  if (typeof cell === "number" && Number.isFinite(cell)) return String(cell);
  if (isNestedArrayWrapper(cell)) {
    const inner = (cell.items ?? []).filter((x): x is string => typeof x === "string");
    return inner[0] ?? "";
  }
  return String(cell);
};

/** Normalize `charts[i].answers` whether stored as string[], number[], or nested wrappers. */
const normalizeChartAnswersRow = (raw: unknown): string[] => {
  if (raw == null) return [];
  if (isNestedArrayWrapper(raw)) {
    return (raw.items as unknown[]).map(coerceAnswerCell);
  }
  if (!Array.isArray(raw)) return [];
  // e.g. answers: [{ firestoreNestedArray: true, items: ['1','0','NA'] }] from nested-array encoding
  if (raw.length === 1 && isNestedArrayWrapper(raw[0])) {
    return ((raw[0] as { items: unknown[] }).items ?? []).map(coerceAnswerCell);
  }
  return raw.map(coerceAnswerCell);
};

const rowHasFilledCell = (row: unknown): boolean =>
  Array.isArray(row) && row.some((c) => String(c ?? "").trim() !== "");

/** Top-level `answers` as one row per chart (handles string[] or string[][] and nested-array cells). */
const flatAnswersFromDoc = (raw: unknown): string[][] => {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const rows = Array.isArray(raw[0]) ? raw : [raw];
  return (rows as unknown[]).map((row) => normalizeChartAnswersRow(row));
};

export const normalizeSubmissionShape = (doc: AnyDoc) => {
  // Empty `charts: []` must not hide top-level `answers` (common on stubs / partial docs).
  if (Array.isArray(doc?.charts) && doc.charts.length > 0) {
    let chartTitles = doc.charts.map((c: any, i: number) => c?.title ?? `Chart ${i + 1}`);
    let answers = doc.charts.map((c: any) => normalizeChartAnswersRow(c?.answers));
    let focus = doc.charts.map((c: any) => {
      const raw = Array.isArray(c?.focus) ? c.focus[0] : typeof c?.focus === "string" ? c.focus : "";
      return toString(raw);
    });
    let treatments = doc.charts.map((c: any) => {
      const raw = Array.isArray(c?.treatments)
        ? c.treatments[0]
        : typeof c?.treatments === "string"
          ? c.treatments
          : "";
      return toString(raw);
    });
    const flatAnswers = flatAnswersFromDoc(doc?.answers);
    const flatFocus = Array.isArray(doc?.focus) ? toStrArray(doc.focus) : [];
    const flatTreatments = Array.isArray(doc?.treatments) ? toStrArray(doc.treatments) : [];
    // Per-chart merge: parent docs often have real compliance cells in `charts[]` for self charts while
    // cross-audit hydration merges slice data into top-level `answers` / `focus` / `treatments` only.
    // A global "all chart rows empty" fallback misses the common case (some rows filled, ตรวจไขว้ rows empty in charts).
    answers = answers.map((chartRow, i) => {
      const flatRow = flatAnswers[i];
      if (rowHasFilledCell(chartRow)) return chartRow;
      if (flatRow && rowHasFilledCell(flatRow)) return flatRow;
      return chartRow;
    });
    focus = focus.map((fv, i) => {
      if (String(fv).trim()) return fv;
      const fl = flatFocus[i] ?? "";
      return String(fl).trim() ? toString(fl) : fv;
    });
    treatments = treatments.map((tv, i) => {
      if (String(tv).trim()) return tv;
      const fl = flatTreatments[i] ?? "";
      return String(fl).trim() ? toString(fl) : tv;
    });

    // Cross-audit hydration merges slice rows into top-level `answers` / `focus` / `treatments` only.
    // Nursing docs may still carry a shorter `charts[]` snapshot; without extending here, rows past
    // `charts.length` are dropped and ตรวจไขว้ reloads as NA/blank in the side panel.
    const topChartTitles = Array.isArray(doc?.chartTitles) ? (doc.chartTitles as unknown[]) : [];
    const structuralEnd = Math.max(
      answers.length,
      flatAnswers.length,
      flatFocus.length,
      flatTreatments.length,
      topChartTitles.length,
    );
    for (let i = answers.length; i < structuralEnd; i++) {
      const flatRow = flatAnswers[i];
      answers.push(Array.isArray(flatRow) ? [...flatRow] : []);
      const ffv = flatFocus[i] ?? "";
      focus.push(String(ffv).trim() ? toString(ffv) : "");
      const tfv = flatTreatments[i] ?? "";
      treatments.push(String(tfv).trim() ? toString(tfv) : "");
      const tt = topChartTitles[i];
      chartTitles.push(
        typeof tt === "string" && String(tt).trim()
          ? String(tt)
          : (DEFAULT_NURSING_LAYOUT_TITLES[i] ?? `Chart ${i + 1}`),
      );
    }

    return { chartTitles, answers, focus, treatments };
  }

  const wrap = (v: any) => (Array.isArray(v) && Array.isArray(v[0]) ? v : Array.isArray(v) ? [v] : []);
  const focus = Array.isArray(doc?.focus) ? toStrArray(doc.focus) : [];
  const treatments = Array.isArray(doc?.treatments) ? toStrArray(doc.treatments) : [];
  return {
    chartTitles: Array.isArray(doc?.chartTitles) ? doc.chartTitles : [],
    answers: wrap(doc?.answers),
    focus,
    treatments,
  };
};

/**
 * First filled compliance answer row for single-chart slices (cross-audit Firestore docs, mirrors).
 * Tries normalized shape, then each `charts[]` entry, then top-level `answers` (incl. encoded rows).
 * Hydration merge uses this so a thin `normalizeSubmissionShape` result cannot drop real Firestore cells.
 */
export function extractPrimaryAnswerRowFromComplianceDoc(doc: AnyDoc): string[] | null {
  if (!doc || typeof doc !== 'object') return null;

  const shape = normalizeSubmissionShape(doc);
  const fromShape = shape.answers[0];
  if (Array.isArray(fromShape) && rowHasFilledCell(fromShape)) {
    return fromShape.map((c) => String(c ?? ''));
  }

  const charts = Array.isArray((doc as { charts?: unknown }).charts)
    ? ((doc as { charts: unknown[] }).charts as unknown[])
    : [];
  for (const c of charts) {
    const rawAns =
      c && typeof c === 'object' && c !== null ? (c as { answers?: unknown }).answers : undefined;
    const row = normalizeChartAnswersRow(rawAns);
    if (rowHasFilledCell(row)) return row;
  }

  const flat = flatAnswersFromDoc((doc as { answers?: unknown }).answers);
  for (const row of flat) {
    if (rowHasFilledCell(row)) return row;
  }

  return null;
}

/** Chart row count for compliance nursing-style payloads (charts[] or flat answers[]). */
export function computeChartCountFromData(data: AnyDoc | null | undefined, fallback: number): number {
  if (!data) return fallback;

  const { answers, focus, treatments, chartTitles } = normalizeSubmissionShape(data);

  const answersLength = answers.length;
  const chartsLength = Array.isArray(data.charts) ? data.charts.length : 0;
  const submittedLength = Array.isArray(data.submitted) ? data.submitted.length : 0;
  const incompleteLength = Array.isArray(data.incomplete) ? data.incomplete.length : 0;

  // Prefer structural row indicators (charts / titles / submitted / answers).
  // A longer `incomplete[]` must not inflate slot count: legacy clients sometimes
  // leave padded incomplete arrays that disagree with real chart rows, which
  // made hub progress stay below 100% even when every real chart was submitted.
  const structuralCount = Math.max(
    chartsLength,
    chartTitles.length,
    submittedLength,
    answersLength,
  );
  if (structuralCount > 0) return structuralCount;
  if (incompleteLength > 0) return incompleteLength;

  const contentLengths = [
    Array.isArray(data.disease) ? data.disease.length : 0,
    focus.length,
    treatments.length,
    Array.isArray(data.problems) ? data.problems.length : 0,
    Array.isArray(data.suggestions) ? data.suggestions.length : 0,
    Array.isArray(data.shifts) ? data.shifts.length : 0,
    Array.isArray(data.dates) ? data.dates.length : 0,
    Array.isArray(data.hn) ? data.hn.length : 0,
    Array.isArray(data.supervisionRecords) ? data.supervisionRecords.length : 0,
    chartTitles.length,
  ];

  const inferred = Math.max(0, ...contentLengths);
  const safe = Number.isFinite(inferred) ? inferred : 0;

  // Category default (`fallback`) must not inflate count after the user deletes charts and saves:
  // stored arrays are authoritative whenever they imply a positive length.
  if (safe === 0) return fallback;

  return safe;
}
